import { useEffect } from 'react';
import type { User } from 'firebase/auth';

import { touchUserPresence, clearUserPresence } from '../config/data/firebase';
import type { UserTypeValue } from '../config/data/collections';
import type { Applicant, Member } from '../types';

const HEARTBEAT_MS = 45 * 1000;

const buildDisplayName = (member: Member | null, applicant: Applicant | null): string => {
	if (member?.firstName) {
		return `${member.firstName} ${member.lastName ?? ''}`.trim();
	}
	if (applicant?.firstName) {
		return `${applicant.firstName} ${applicant.lastName ?? ''}`.trim();
	}
	return 'User';
};

export const usePresenceHeartbeat = (
	user: User | null,
	role: UserTypeValue | null,
	member: Member | null,
	applicant: Applicant | null
) => {
	useEffect(() => {
		if (!user?.uid || user.isAnonymous) {
			return;
		}

		const displayName = buildDisplayName(member, applicant);

		const ping = () => {
			touchUserPresence({ uid: user.uid, role, displayName }).catch(() => {});
		};

		ping();
		const intervalId = window.setInterval(ping, HEARTBEAT_MS);

		const handleVisibility = () => {
			if (document.visibilityState === 'visible') {
				ping();
			}
		};
		document.addEventListener('visibilitychange', handleVisibility);

		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener('visibilitychange', handleVisibility);
			clearUserPresence(user.uid).catch(() => {});
		};
	}, [user?.uid, user?.isAnonymous, role, member, applicant]);
};
